import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Plus, 
  Shield,
  Users,
  Trash2,
  User,
  Mail
} from 'lucide-react';

const EP3D_LOGO = "https://customer-assets.emergentagent.com/job_sales-force-hub-3/artifacts/8yq7y2ex_Logotipo_eng3d_2-removebg-preview%20%281%29.gif";

const ROLES = [
  { id: 'admin', label: 'Administrador', description: 'Acesso total ao sistema' },
  { id: 'gestor', label: 'Gestor Comercial', description: 'Pode gerir clientes e encomendas' },
  { id: 'vendedor', label: 'Vendedor', description: 'Acesso às suas próprias rotas e clientes' }
];

export default function AdminPage() {
  const { api, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'vendedor'
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const response = await api().get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api().post('/admin/users', form);
      toast.success('Utilizador criado!');
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar utilizador');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Eliminar utilizador "${userName}"?`)) return;
    
    try {
      await api().delete(`/admin/users/${userId}`);
      toast.success('Utilizador eliminado!');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar utilizador');
    }
  };

  const handleCommissionUpdate = async (userId, commissionRate) => {
    try {
      await api().put(`/admin/users/${userId}/commission`, { commission_rate: commissionRate });
      toast.success('Comissão atualizada!');
    } catch (error) {
      toast.error('Erro ao atualizar comissão');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      password: '',
      role: 'vendedor'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'gestor': return 'bg-blue-100 text-blue-800';
      case 'vendedor': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold font-[Manrope]">Acesso Restrito</h3>
          <p className="text-muted-foreground mt-1">Apenas administradores podem aceder a esta página</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header with Logo */}
      <Card className="bg-slate-900 text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={EP3D_LOGO} alt="EP3D" className="h-16 w-auto" />
              <div>
                <h1 className="text-2xl font-bold font-[Manrope] text-amber-400">VENDAS</h1>
                <p className="text-slate-400">Painel de Administração</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Administrador</p>
              <p className="font-medium">{user?.name}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Administradores</p>
                <p className="text-2xl font-bold font-[Manrope]">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gestores Comerciais</p>
                <p className="text-2xl font-bold font-[Manrope]">
                  {users.filter(u => u.role === 'gestor').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <User className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Vendedores</p>
                <p className="text-2xl font-bold font-[Manrope]">
                  {users.filter(u => u.role === 'vendedor').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-[Manrope]">Gestão de Utilizadores</CardTitle>
              <CardDescription>Crie e gerencie os utilizadores do sistema</CardDescription>
            </div>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={() => { resetForm(); setShowModal(true); }}
              data-testid="new-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Utilizador
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>UTILIZADOR</TableHead>
                  <TableHead>EMAIL</TableHead>
                  <TableHead>FUNÇÃO</TableHead>
                  <TableHead>COMISSÃO</TableHead>
                  <TableHead className="w-[100px]">AÇÕES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-amber-100 text-amber-800 font-bold">
                            {getInitials(u.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          {u.id === user?.id && (
                            <Badge variant="outline" className="text-xs">Você</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {u.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(u.role)}>
                        {ROLES.find(r => r.id === u.role)?.label || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          className="w-20 h-8"
                          defaultValue={u.commission_rate || 0}
                          onBlur={(e) => handleCommissionUpdate(u.id, parseFloat(e.target.value) || 0)}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {u.id !== user?.id && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-500"
                          onClick={() => handleDelete(u.id, u.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Roles Info */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope]">Permissões por Função</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ROLES.map((role) => (
              <div key={role.id} className={`p-4 rounded-lg border ${getRoleColor(role.id).replace('text-', 'border-').split(' ')[0]}`}>
                <Badge className={getRoleColor(role.id)}>{role.label}</Badge>
                <p className="text-sm text-muted-foreground mt-2">{role.description}</p>
                <ul className="mt-3 text-xs space-y-1">
                  {role.id === 'admin' && (
                    <>
                      <li>• Criar produtos</li>
                      <li>• Criar utilizadores</li>
                      <li>• Ver todas as rotas e encomendas</li>
                      <li>• Acesso total</li>
                    </>
                  )}
                  {role.id === 'gestor' && (
                    <>
                      <li>• Criar clientes</li>
                      <li>• Criar encomendas</li>
                      <li>• Gerir rotas</li>
                      <li>• Ver equipa</li>
                    </>
                  )}
                  {role.id === 'vendedor' && (
                    <>
                      <li>• Ver clientes</li>
                      <li>• Registar atividades</li>
                      <li>• Fazer check-in nas rotas</li>
                    </>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Novo Utilizador</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Palavra-passe *</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  data-testid="user-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Função</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-user-btn">
                Criar Utilizador
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
